var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import * as Enums from "./enums";
import { Strings } from "./strings";
import { GlobalSettings } from "./shared";
import { HostCapabilities } from "./host-capabilities";
import { Versions, property, SerializableObject, StringProperty, SerializableObjectProperty } from "./serialization";
export class ValidationResults {
    constructor() {
        this.allIds = {};
        this.validationEvents = [];
    }
    addFailure(cardObject, event, message) {
        this.validationEvents.push({
            phase: Enums.ValidationPhase.Validation,
            source: cardObject,
            event: event,
            message: message
        });
    }
}
export class CardObject extends SerializableObject {
    constructor() {
        //#region Schema
        super(...arguments);
        //#endregion
        this._shouldFallback = false;
    }
    getSchemaKey() {
        return this.getJsonTypeName();
    }
    get requires() {
        return this.getValue(CardObject.requiresProperty);
    }
    /**
     * Checks if this CardObject contains the given DOM Node.
     * @param node The DOM Node to look for.
     * @returns `true` if the DOM Node was found, `false` otherwise.
     */
    contains(node) {
        if (this._renderedElement) {
            return this._renderedElement.contains(node);
        }
        return false;
    }
    preProcessPropertyValue(prop, propertyValue) {
        const value = propertyValue === undefined ? this.getValue(prop) : propertyValue;
        if (GlobalSettings.allowPreProcessingPropertyValues) {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            let currentObject = this;
            while (currentObject && !currentObject.onPreProcessPropertyValue) {
                currentObject = currentObject.parent;
            }
            if (currentObject && currentObject.onPreProcessPropertyValue) {
                return currentObject.onPreProcessPropertyValue(this, prop, value);
            }
        }
        return value;
    }
    setParent(value) {
        this._parent = value;
    }
    setShouldFallback(value) {
        this._shouldFallback = value;
    }
    shouldFallback() {
        return this._shouldFallback || !this.requires.areAllMet(this.hostConfig.hostCapabilities);
    }
    getRootObject() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let currentObject = this;
        while (currentObject.parent) {
            currentObject = currentObject.parent;
        }
        return currentObject;
    }
    internalValidateProperties(context) {
        if (this.id) {
            if (context.allIds.hasOwnProperty(this.id)) {
                if (context.allIds[this.id] === 1) {
                    context.addFailure(this, Enums.ValidationEvent.DuplicateId, Strings.errors.duplicateId(this.id));
                }
                context.allIds[this.id] += 1;
            }
            else {
                context.allIds[this.id] = 1;
            }
        }
    }
    validateProperties() {
        const result = new ValidationResults();
        this.internalValidateProperties(result);
        return result;
    }
    /**
     * Recursively searches this CardObject and any children to find the
     * innermost CardObject that owns the given DOM Node.
     *
     * @param node The DOM Node to look for.
     *
     * @returns The owner of the given DOM Node, or `undefined` if no owner was found.
     */
    findDOMNodeOwner(node) {
        // default implementation for CardObjects with no associated children
        return this.contains(node) ? this : undefined;
    }
    releaseDOMResources() {
        // Do nothing in base implementation
    }
    get parent() {
        return this._parent;
    }
    get renderedElement() {
        return this._renderedElement;
    }
}
CardObject.typeNameProperty = new StringProperty(Versions.v1_0, "type", undefined, undefined, undefined, (sender) => {
    return sender.getJsonTypeName();
});
CardObject.idProperty = new StringProperty(Versions.v1_0, "id");
CardObject.requiresProperty = new SerializableObjectProperty(Versions.v1_2, "requires", HostCapabilities, false, new HostCapabilities());
__decorate([
    property(CardObject.idProperty)
], CardObject.prototype, "id", void 0);
__decorate([
    property(CardObject.requiresProperty)
], CardObject.prototype, "requires", null);
//# sourceMappingURL=card-object.js.map